"use no memo";

// Column is a mutable TanStack Table v8 instance shared with DataTable.
// Remove this boundary after upgrading to a compiler-compatible TanStack Table release.
import { ArrowDownIcon, ArrowUpIcon, CaretSortIcon, EyeNoneIcon } from "@radix-ui/react-icons";
import { Column } from "@tanstack/react-table";
import { ListFilter } from "lucide-react";
import * as React from "react";
import { Button } from "@/src/components/shadcn/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/src/components/shadcn/ui/dropdown-menu";
import { Popover, PopoverContent, PopoverTrigger } from "@/src/components/shadcn/ui/popover";
import { Separator } from "@/src/components/shadcn/ui/separator";
import { cn } from "@/src/lib/shadcn-utils";
interface DataTableColumnHeaderProps<TData, TValue> extends React.HTMLAttributes<HTMLDivElement> {
  column: Column<TData, TValue>;
  title: string;
  ascendingLabel?: string;
  descendingLabel?: string;
  /**
   * Inline filter UI rendered inside the same column-header popover, beneath the
   * sort/hide actions. When provided, the header uses a single Popover (instead
   * of the sort-only DropdownMenu) so sorting and filtering share one surface.
   */
  filterContent?: React.ReactNode;
  /** Whether the column currently has an active filter (drives the header indicator). */
  isFiltered?: boolean;
}
function SortIndicator({
  sorted
}: {
  sorted: false | "asc" | "desc";
}) {
  if (sorted === "desc") return <ArrowDownIcon className="ml-2 h-4 w-4" />;
  if (sorted === "asc") return <ArrowUpIcon className="ml-2 h-4 w-4" />;
  return <CaretSortIcon className="ml-2 h-4 w-4" />;
}
export function DataTableColumnHeader<TData, TValue>({
  column,
  title,
  ascendingLabel = "Asc",
  descendingLabel = "Desc",
  className,
  filterContent,
  isFiltered = false
}: DataTableColumnHeaderProps<TData, TValue>) {
  const [open, setOpen] = React.useState(false);
  const canSort = column.getCanSort();
  const canHide = column.getCanHide();
  const sorted = column.getIsSorted();

  // Combined header menu: a single Popover holding sort/hide actions and the
  // column's inline filter. Used whenever a column exposes a filter.
  if (filterContent) {
    return <div className={cn("flex items-center", className)}>
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button variant="ghost" size="sm" className="-ml-3 h-8 data-[state=open]:bg-accent">
              <span>{title.toUpperCase()}</span>
              {isFiltered && <ListFilter className="ml-2 h-3.5 w-3.5 text-primary" />}
              {canSort && (sorted || !isFiltered) && <SortIndicator sorted={sorted} />}
            </Button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-[220px] p-0">
            {(canSort || canHide) && <div className="flex flex-col p-1">
                {canSort && <>
                    <button type="button" onClick={() => {
                column.toggleSorting(false);
                setOpen(false);
              }} className="flex items-center rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-accent hover:text-accent-foreground">
                      <ArrowUpIcon className="mr-2 h-3.5 w-3.5 text-muted-foreground/70" />
                      {ascendingLabel}
                    </button>
                    <button type="button" onClick={() => {
                column.toggleSorting(true);
                setOpen(false);
              }} className="flex items-center rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-accent hover:text-accent-foreground">
                      <ArrowDownIcon className="mr-2 h-3.5 w-3.5 text-muted-foreground/70" />
                      {descendingLabel}
                    </button>
                  </>}
                {canHide && <button type="button" onClick={() => {
              column.toggleVisibility(false);
              setOpen(false);
            }} className="flex items-center rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-accent hover:text-accent-foreground">
                    <EyeNoneIcon className="mr-2 h-3.5 w-3.5 text-muted-foreground/70" />
                    Hide
                  </button>}
              </div>}
            {(canSort || canHide) && <Separator />}
            {filterContent}
          </PopoverContent>
        </Popover>
      </div>;
  }
  if (!canSort) {
    return <span className={cn(className, "text-xs")}>{title.toUpperCase()}</span>;
  }
  return <div className={cn("flex items-center", className)}>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="sm" className="-ml-3 h-8 data-[state=open]:bg-accent">
            <span>{title.toUpperCase()}</span>
            <SortIndicator sorted={sorted} />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start">
          <DropdownMenuItem onClick={() => column.toggleSorting(false)}>
            <ArrowUpIcon className="mr-2 h-3.5 w-3.5 text-muted-foreground/70" />
            {ascendingLabel}
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => column.toggleSorting(true)}>
            <ArrowDownIcon className="mr-2 h-3.5 w-3.5 text-muted-foreground/70" />
            {descendingLabel}
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => column.toggleVisibility(false)}>
            <EyeNoneIcon className="mr-2 h-3.5 w-3.5 text-muted-foreground/70" />
            Hide
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>;
}